import { IDatasetRequestOptions } from '@infotech-soe/merps/types/Queriesn';
import { IMetaSchemaSource } from '@infotech-soe/metaschema';
import { decode, encode } from 'base64-arraybuffer';

import { EventDispatcher } from '../../../framework/events/EventDispatcher';
import JstpClient from '../../network/JstpClient';
import { RemoteEvent } from '../../network/RemoteEvent';
import IApiProvider, {
  ActionApiAdapter,
  AuthApiAdapter,
  CatalogApiAdapter,
  DatabaseService,
  DisplayModeApiAdapter,
  DocLinksApiAdapter,
  IntlApiAdapter,
  NotificationApiAdapter,
  PrintApiAdapter,
  PrivsApiAdapter,
  SchemaApiAdapter,
  StreamApiAdapter,
  TreeApiAdapter,
  UserConfigApiAdapter,
  UserOptionsService,
  WorkTimesService,
} from '../abstract/IApiProvider';

const MAX_CHUNK_SIZE = Number(process.env.MAX_CHUNK_SIZE) || 1000000;
const NOTIFICATION_CLEANUP_INTERVAL = 60000;

class MerpsApiProviderSingleton implements IApiProvider {
  private serializeData(data: Record<string, any>): string {
    return JSON.stringify(data);
  }

  private deserializeData(data: string, emptyData: any = {}): any {
    return data ? JSON.parse(data) : emptyData;
  }

  private readonly eventBus = new EventDispatcher<Record<string, any>>();
  public readonly rpc = new JstpClient();

  public connect(
    url: string,
    options?: { heartbeatInterval?: number; maxDumbHeartbeats?: number }
  ): Promise<void> {
    return this.rpc.connect(url, 'bravo', options);
  }

  public readonly db: DatabaseService = {
    getById: (source, id, field?) => {
      return this.rpc.callMethod('queries', 'getById', [source, id, field]);
    },
  };

  public readonly displayModes: DisplayModeApiAdapter = {
    getDataset: (
      category,
      displayMode,
      dataset,
      params,
      processId,
      conditions?,
      treeState?,
      sort?,
      linksGraph?,
      rowsLimit?,
      totals?
    ) => {
      const options: IDatasetRequestOptions = {
        conditions,
        treeState,
        sort,
        linksGraph,
        rowsLimit,
        totals,
      };

      return this.rpc.callMethod('queries', 'getDataset', [
        category,
        displayMode,
        dataset,
        params,
        options,
        processId,
      ]);
    },

    getDatasetRecord: (category, displayMode, dataset, id) => {
      return this.rpc.callMethod('queries', 'getDatasetRecord', [
        category,
        displayMode,
        dataset,
        id,
      ]);
    },

    getByPosParams: (category, displayMode, params) => {
      return this.rpc.callMethod('queries', 'getByPosParams', [
        category,
        displayMode,
        params,
      ]);
    },
  };

  public readonly intl: IntlApiAdapter = {
    getCategory: async (category, locale?) => {
      return this.deserializeData(
        await this.rpc.callMethod('l10n', 'getCategory', [category, locale])
      );
    },

    getCommon: async (locale?) => {
      return this.deserializeData(
        await this.rpc.callMethod('l10n', 'getCommon', [locale])
      );
    },

    getDomains: async (locale?) => {
      return this.deserializeData(
        await this.rpc.callMethod('l10n', 'getDomains', [locale])
      );
    },
  };

  public readonly userConfig: UserConfigApiAdapter = {
    saveConditionsConfig: (category, displayMode, form, data) => {
      return this.rpc.callMethod('user', 'saveConditionsConfig', [
        category,
        displayMode,
        form,
        this.serializeData(data),
      ]);
    },

    getConditionsConfig: async (
      category,
      displayMode,
      form,
      withDefaultConfig?
    ) => {
      return this.deserializeData(
        await this.rpc.callMethod('user', 'getConditionsConfig', [
          category,
          displayMode,
          form,
          withDefaultConfig,
        ])
      );
    },

    savePanelLinks: data => {
      return this.rpc.callMethod('user', 'savePanelLinks', [
        'panelLinks',
        this.serializeData(data),
      ]);
    },

    getPanelLinks: async () => {
      return this.deserializeData(
        await this.rpc.callMethod('user', 'getPanelLinks', ['panelLinks']),
        []
      );
    },

    saveDisplayModeConfig: (category, displayMode, data) => {
      return this.rpc.callMethod('user', 'saveDisplayModeConfig', [
        category,
        displayMode,
        this.serializeData(data),
      ]);
    },

    getDisplayModeConfig: async (category, displayMode, withDefaultConfig?) => {
      return this.deserializeData(
        await this.rpc.callMethod('user', 'getDisplayModeConfig', [
          category,
          displayMode,
          withDefaultConfig,
        ])
      );
    },

    saveFormConfig: (category, form, data) => {
      return this.rpc.callMethod('user', 'saveFormConfig', [
        category,
        form,
        this.serializeData(data),
      ]);
    },

    getFormConfig: async (category, form) => {
      return this.deserializeData(
        await this.rpc.callMethod('user', 'getFormConfig', [category, form])
      );
    },

    savePivotConfig: (category, code, data) => {
      return this.rpc.callMethod('user', 'savePivotConfig', [
        category,
        code,
        this.serializeData(data),
      ]);
    },

    getPivotConfig: async (category, code) => {
      return this.deserializeData(
        await this.rpc.callMethod('user', 'getPivotConfig', [category, code])
      );
    },

    saveGeneralConfig: data => {
      return this.rpc.callMethod('user', 'saveGeneralConfig', [
        this.serializeData(data),
      ]);
    },

    getGeneralConfig: async () => {
      return this.deserializeData(
        await this.rpc.callMethod('user', 'getGeneralConfig', [])
      );
    },
  };

  public readonly auth: AuthApiAdapter = {
    signIn: (login, password, application, newPassword, signature?) => {
      return this.rpc.callMethod('auth', 'login', [
        login,
        password,
        application,
        newPassword,
        signature,
      ]);
    },

    signInWithWindowsAD: (application, userName, password) => {
      return this.rpc.callMethod('auth', 'loginWithWindowsAD', [
        userName,
        password,
        application,
      ]);
    },

    loginWithKerberos: application => {
      return this.rpc.callMethod('auth', 'loginWithKerberos', [application]);
    },

    signOut: async () => {
      await this.rpc.callMethod('auth', 'logout', []);
      this.rpc.disconnect();
    },

    getAllowedCategories: () => {
      return this.rpc.callMethod('user', 'getCategories', []);
    },

    getSchemaCrc: () => {
      return this.rpc.callMethod('schemas', 'getSchemaCrc', []);
    },

    getFeaturesList: () => {
      return this.rpc.callMethod('auth', 'getFeatureList', []);
    },

    getProjectInfo: () => {
      return this.rpc.callMethod('schemas', 'getProjectInfo', []);
    },
  };

  public readonly actions: ActionApiAdapter = {
    executeAction: (action, context, args, categoryName?) => {
      return this.rpc.callMethod('queries', 'execute', [
        action,
        context,
        args,
        categoryName,
      ]);
    },
    getActionArgs: (action, context) => {
      return this.rpc.callMethod('queries', 'getAttributes', [action, context]);
    },
    generateProcessId: () => {
      return this.rpc.callMethod('queries', 'generateProcessId', []);
    },
    addListenerOnActionProgress: cb => {
      this.eventBus.addListener(payload => {
        if (
          payload.interfaceName === 'actions' &&
          payload.eventName === 'progress'
        ) {
          cb({
            progress: payload.progress,
            message: payload.message,
            cancellable: payload.cancellable,
          });
        }
      });
    },
    cancelAction: async () => {
      await this.rpc.callMethod('queries', 'cancelAction', []);
    },
  };

  public readonly schemas: SchemaApiAdapter = {
    addListenerOnSchemasUpdate: cb => {
      this.eventBus.addListener(async event => {
        if (event.type === 'version') {
          await this.rpc.callMethod('user', 'updateSchemas', []);
          cb(event.version);
          this.eventBus.dispatch({
            type: 'updated',
          });
        }
      });
    },
    addListenerOnSchemasUpdated: cb => {
      this.eventBus.addListener(({ type }) => type === 'updated' && cb());
    },

    getConditionsSetSchema: async name => {
      const allSchemas: IMetaSchemaSource[] = await this.rpc.callMethod(
        'schemas',
        'getSchema',
        [null]
      );

      const schemas = allSchemas
        .filter(schema => schema.source)
        .map((schema: any) => {
          if (schema.module) {
            schema.category = schema.module;
          }
          return schema;
        });

      const conditionsSetSchema = schemas.some(
        schema => schema.type === 'conditions' && schema.name === name
      );

      if (!conditionsSetSchema) {
        throw new Error('No such conditions set: ' + name);
      }

      return schemas;
    },

    getDomainsSchema: () => {
      return this.rpc.callMethod('schemas', 'getDomains', []);
    },

    getCategorySchema: async name => {
      return [await this.rpc.callMethod('schemas', 'getCategory', [name])];
    },

    getViewSchema: async name => {
      return [await this.rpc.callMethod('schemas', 'getView', [name])];
    },

    getFormSchema: (category, form) => {
      return this.rpc.callMethod('schemas', 'getCategoryData', [
        'forms',
        category,
        form,
      ]);
    },

    getActionSchema: (category, action) => {
      return this.rpc.callMethod('schemas', 'getCategoryData', [
        'actions',
        category,
        action,
      ]);
    },

    getDisplayModeSchema: (category, displayMode?) => {
      return this.rpc.callMethod('schemas', 'getCategoryData', [
        'displayModes',
        category,
        displayMode,
      ]);
    },

    getDatasetCategoryName: (category, displayMode, dataset) => {
      return this.rpc.callMethod('schemas', 'getDatasetCategoryName', [
        category,
        displayMode,
        dataset,
      ]);
    },

    getApplicationSchema: async application => {
      return [
        await this.rpc.callMethod('schemas', 'getApplication', [application]),
      ];
    },

    getMenuSchema: async application => {
      return [await this.rpc.callMethod('schemas', 'getMenu', [application])];
    },

    getTemplate: async (kind, name) => {
      return [
        await this.rpc.callMethod('schemas', 'getTemplate', [kind, name]),
      ];
    },

    getField: (category, path) => {
      return this.rpc.callMethod('schemas', 'getField', [category, path]);
    },

    getOptionSchema: name => {
      return this.rpc.callMethod('schemas', 'getOptionSchema', [name]);
    },

    getPrimaryField: category => {
      return this.rpc.callMethod('schemas', 'getPrimaryField', [category]);
    },

    getIconFont: () => this.rpc.callMethod('schemas', 'getIconFont', []),
  };

  public readonly catalogs: CatalogApiAdapter = {
    createCatalog: (parentId, name) => {
      return this.rpc.callMethod('catalogs', 'create', [parentId, name]);
    },

    deleteCatalog: async id => {
      await this.rpc.callMethod('catalogs', 'delete', [id]);
    },

    moveCatalog: async (id, newParentId) => {
      await this.rpc.callMethod('catalogs', 'move', [id, newParentId]);
    },

    renameCatalog: async (id, newName) => {
      await this.rpc.callMethod('catalogs', 'rename', [id, newName]);
    },

    getCatalogIdByName: (categoryName, value) => {
      return this.rpc.callMethod('catalogs', 'getCatalogIdByName', [
        categoryName,
        value,
      ]);
    },
  };

  public readonly tree: TreeApiAdapter = {
    get: (parentId, category, displayMode, params) => {
      return this.rpc.callMethod('tree', 'get', [
        parentId,
        category,
        displayMode,
        params,
      ]);
    },
    getPath: (nodeId, category, displayMode) => {
      return this.rpc.callMethod('tree', 'getPath', [
        nodeId,
        category,
        displayMode,
      ]);
    },

    getRecursive: (parentId, category, displayMode, params) => {
      return this.rpc.callMethod('tree', 'getRecursive', [
        parentId,
        category,
        displayMode,
        params,
      ]);
    },
  };

  public readonly streams: StreamApiAdapter = {
    write: async file => {
      const streamId = await this.rpc.callMethod('streams', 'write', [
        null,
        null,
      ]);

      let prevChunkWritten = Promise.resolve();

      for (let i = 0; i < file.byteLength; i += MAX_CHUNK_SIZE) {
        const chunk = encode(file.slice(i, i + MAX_CHUNK_SIZE));
        await prevChunkWritten;
        prevChunkWritten = this.rpc.callMethod('streams', 'write', [
          streamId,
          chunk,
        ]);
      }

      await prevChunkWritten;

      await this.rpc.callMethod('streams', 'write', [streamId, null]);

      return streamId;
    },
    read: async id => {
      const chunks = [];

      let result = await this.rpc.callMethod('streams', 'read', [id]);
      while (result !== null) {
        chunks.push(decode(result));
        result = await this.rpc.callMethod('streams', 'read', [id]);
      }

      let length = 0;
      for (const chunk of chunks) {
        length += chunk.byteLength;
      }

      const resultingArray = new Uint8Array(length);

      let offset = 0;
      for (const chunk of chunks) {
        resultingArray.set(new Uint8Array(chunk), offset);
        offset += chunk.byteLength;
      }

      return resultingArray.buffer;
    },
    prepareStoredFile: id => {
      return this.rpc.callMethod('streams', 'prepareStoredFile', [id]);
    },
  };

  public readonly notifications: NotificationApiAdapter = {
    addListenerOnNewNotification: cb => {
      this.eventBus.addListener(payload => {
        if (
          payload.interfaceName === 'notifications' ||
          payload.interfaceName === 'print'
        ) {
          cb(payload);
        }
      });
    },
    getUserNotifications: () => {
      return this.rpc.callMethod('user', 'listNotifications', []);
    },
    deleteNotification: async id => {
      await this.rpc.callMethod('user', 'deleteNotification', [id]);
    },
  };

  public readonly docLinks: DocLinksApiAdapter = {
    getGraph: (category, docId) => {
      return this.rpc.callMethod('docLinks', 'graph', [category, docId]);
    },
  };

  public readonly print: PrintApiAdapter = {
    getExportedReport: (action, context, args) => {
      return this.rpc.callMethod('print', 'getExportedReport', [
        action,
        context,
        args,
      ]);
    },
    getReportDataFromPrintAction: (action, context, args) => {
      return this.rpc.callMethod('print', 'getReportDataFromPrintAction', [
        action,
        context,
        args,
      ]);
    },
    getTemplateChecksum: name => {
      return this.rpc.callMethod('print', 'getTemplateChecksum', [name]);
    },
    getTemplate: name => {
      return this.rpc.callMethod('print', 'getTemplate', [name]);
    },
    getReportDataFromLibraryRoutine: (
      categoryName,
      displayModeName,
      datasetName,
      recordViewName,
      id
    ) => {
      return this.rpc.callMethod('print', 'getReportDataFromLibraryRoutine', [
        categoryName,
        displayModeName,
        datasetName,
        recordViewName,
        id,
      ]);
    },
  };

  public readonly options: UserOptionsService = {
    getOption: name => {
      return this.rpc.callMethod('user', 'getOption', [name]);
    },
  };

  public readonly rolePrivs: PrivsApiAdapter = {
    getPanes: categoryName => {
      return this.rpc.callMethod('privs', 'getPanes', [categoryName]);
    },
    getTree: (categoryName, roleId, treeType, nodeId?) => {
      return this.rpc.callMethod('privs', 'getTree', [
        categoryName,
        roleId,
        treeType,
        nodeId,
      ]);
    },
    getCategoryTree: (categoryName, nodeId?) => {
      return this.rpc.callMethod('privs', 'getCategoryTree', [
        categoryName,
        nodeId,
      ]);
    },
    getCatalogActions: (categoryName, roleId, catalogId) => {
      return this.rpc.callMethod('privs', 'getCatalogActions', [
        categoryName,
        roleId,
        catalogId,
      ]);
    },
    getCategoryActions: (categoryName, roleId, nodeIds) => {
      return this.rpc.callMethod('privs', 'getCategoryActions', [
        categoryName,
        roleId,
        nodeIds,
      ]);
    },
    setMainTreeNodePrivs: async (
      categoryName,
      roleId,
      pane,
      nodeId,
      grantFlag,
      inheritHier
    ) => {
      const methodsNamesMap = {
        catalog: 'setCatalogTreeNodePrivs',
        hierarchy: 'setHierarchyTreeNodePrivs',
        subdivision: 'setSubdivisionTreeNodePrivs',
      } as const;
      return this.rpc.callMethod('privs', methodsNamesMap[pane], [
        categoryName,
        roleId,
        nodeId,
        grantFlag,
        inheritHier,
      ]);
    },
    setCatalogActionPrivs: async (
      categoryName,
      roleId,
      nodeId,
      funcCode,
      grantFlag,
      inheritHier
    ) => {
      await this.rpc.callMethod('privs', 'setCatalogActionPrivs', [
        categoryName,
        roleId,
        nodeId,
        funcCode,
        grantFlag,
        inheritHier,
      ]);
    },
    setCategoryActionPrivs: async (
      categoryName,
      roleId,
      nodeIds,
      funcCode,
      grantFlag,
      inheritHier
    ) => {
      await this.rpc.callMethod('privs', 'setCategoryActionPrivs', [
        categoryName,
        roleId,
        nodeIds,
        funcCode,
        grantFlag,
        inheritHier,
      ]);
    },
    setCatalogAllActionsPrivs: async (
      categoryName,
      roleId,
      catalogId,
      inheritHier
    ) => {
      await this.rpc.callMethod('privs', 'setCatalogAllActionsPrivs', [
        categoryName,
        roleId,
        catalogId,
        inheritHier,
      ]);
    },
    setCategoryAllActionsPrivs: async (
      categoryName,
      roleId,
      nodeIds,
      inheritHier
    ) => {
      await this.rpc.callMethod('privs', 'setCategoryAllActionsPrivs', [
        categoryName,
        roleId,
        nodeIds,
        inheritHier,
      ]);
    },
  };

  public readonly workTimes: WorkTimesService = {
    getWorkTimes: (postId, dateFrom, dateTo, plan = false) => {
      return this.rpc.callMethod('worktimes', 'get', [
        postId,
        dateFrom,
        dateTo,
        plan,
      ]);
    },
    updateWorkTimes: (postId, date, hours, days, plan = false) => {
      return this.rpc.callMethod('worktimes', 'update', [
        postId,
        date,
        days,
        hours,
        plan,
      ]);
    },
    getTimeSheet: timeSheetId => {
      return this.rpc.callMethod('worktimes', 'getTimeSheet', [timeSheetId]);
    },
    updateTimeSheet: (timeSheetId, hours, days) => {
      return this.rpc.callMethod('worktimes', 'updateTimeSheet', [
        timeSheetId,
        days,
        hours,
      ]);
    },
    getTimeBoard: timeBoardId => {
      return this.rpc.callMethod('worktimes', 'getTimeBoard', [timeBoardId]);
    },
    updateTimeBoard: (timeBoardId, hours, days) => {
      return this.rpc.callMethod('worktimes', 'updateTimeBoard', [
        timeBoardId,
        days,
        hours,
      ]);
    },
  };

  constructor() {
    setInterval(
      () =>
        this.eventBus.dispatch({
          interfaceName: 'notifications',
          eventName: 'system-update',
        }),
      NOTIFICATION_CLEANUP_INTERVAL
    );
    this.rpc.onRemoteEvent.addListener(
      RemoteEvent.listenerFor('schemas', 'updated', version => {
        this.eventBus.dispatch({
          type: 'version',
          version,
        });
      })
    );
    this.rpc.onRemoteEvent.addListener(
      RemoteEvent.listenerFor('print', 'done', notification =>
        this.eventBus.dispatch({
          interfaceName: 'print',
          eventName: 'done',
          notification,
        })
      )
    );
    this.rpc.onRemoteEvent.addListener(
      RemoteEvent.listenerFor('notifications', 'system', notification => {
        this.eventBus.dispatch({
          interfaceName: 'notifications',
          eventName: 'system',
          notification,
        });
      })
    );
    this.rpc.onRemoteEvent.addListener(
      RemoteEvent.listenerFor(
        'notifications',
        'system-delete',
        notificationId => {
          this.eventBus.dispatch({
            interfaceName: 'notifications',
            eventName: 'system-delete',
            notificationId,
          });
        }
      )
    );
    this.rpc.onRemoteEvent.addListener(
      RemoteEvent.listenerFor('actions', 'progress', payload => {
        this.eventBus.dispatch({
          interfaceName: 'actions',
          eventName: 'progress',
          ...payload,
        });
      })
    );
  }
}

const MerpsApiProvider = (() => {
  let instance: IApiProvider;
  return new Proxy(MerpsApiProviderSingleton, {
    construct(Target: typeof MerpsApiProviderSingleton): IApiProvider {
      if (!instance) {
        instance = new Target();
      }
      return instance;
    },
  });
})();

export default MerpsApiProvider;
